import React, {useCallback, useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent, useEffectItem} from 'helpers/hooks/utils';
import {useTable} from 'components/organisms/Providers/TableProvider/TableProvider';
import constants from 'helpers/constants';
import InlineForm from 'components/organisms/Forms/InlineForm/InlineForm';
import TasksBarChart from 'components/organisms/Charts/TasksBarChart/TasksBarChart';
import Box from 'components/atoms/Layout/Box/Box';
import StyledCollectionEntitiesTaskBreakdownGraphCard
  from 'components/organisms/Cards/CollectionEntitiesTaskBreakdownGraphCard/CollectionEntitiesTaskBreakdownGraphCard.styles';
import {useClientStatisticTaskBreakdown} from 'services/client/statistic/statistic.utils';
import {P} from 'components/atoms/Text/Typography/Typography';
import utils from 'helpers/utils';
import SimpleLoader from 'components/molecules/Loaders/SimpleLoader/SimpleLoader';

const CollectionEntitiesTaskBreakdownGraphCard = React.forwardRef((props, ref) => {
  const innerProps = useComponentProps(props, 'CollectionEntitiesTaskBreakdownGraphCard');

  const tableProvider = useTable();
  const collection = tableProvider.context?.data;
  const graphState = tableProvider.graphState;

  const [breakdown, setBreakdown] = useState(null);

  const listState = useEffectItem(tableProvider.appliedListState());
  const year = graphState.graphSettings['tasks']?.filter?.year;
  const months = graphState.graphSettings['tasks']?.filter?.months ?? 1;

  const [tasksBreakdown, isLoading] = useClientStatisticTaskBreakdown(
    year, months, collection?.collectionId,
    listState.search, listState.filter,
    {
      enabled: collection?.collectionId > 0
    });

  useEffect(() => {
    if (tasksBreakdown) {
      setBreakdown(tasksBreakdown);
    }
  }, [tasksBreakdown]);

  const fields = useMemo(() => {
    const initialOption = constants.data.periodMonths.find((opt) => opt.value === months);

    return [{
      name: 'months',
      type: constants.formFieldTypes.autocomplete,
      validation: constants.formFieldValidationTypes.text,
      placeholder: 'Select a period',
      initial: initialOption,
      FormFieldProps: {
        autoFocus: false,
        hiddenLabel: true,
        clearable: false,
        size: 'small'
      },
      options: constants.data.periodMonths
    }];
  }, [months]);

  const setGraphSettingsEvent = useEffectEvent(graphState.setGraphSettings);
  const handleFilterChange = useCallback((field, value) => {
    setGraphSettingsEvent?.((current) => ({
      ...current,
      'tasks': {
        ...current?.['tasks'],
        filter: {
          ...current?.['tasks']?.filter,
          months: value?.value ?? value
        }
      }
    }));
  }, [setGraphSettingsEvent]);

  const handleVisibilityChange = (visibility) => {
    graphState.setGraphSettings((current) => ({
      ...current,
      'tasks': {
        ...current?.['tasks'],
        visibility
      }
    }));
  }

  return <StyledCollectionEntitiesTaskBreakdownGraphCard ref={ref} {...innerProps}
                                                         title="Tasks"
                                                         context={(fields ? <InlineForm onChange={handleFilterChange}
                                                                                        fields={fields} /> : null)}>
    {(utils.isDefined(breakdown) && breakdown?.length > 0) ?
      <Box className="CollectionEntitiesTaskBreakdownGraphCard-content">
        <TasksBarChart breakdown={breakdown}
                       showLegend={false}
                       isLoading={isLoading}
                       visibility={graphState.graphSettings['tasks']?.visibility}
                       onVisibilityChange={handleVisibilityChange} />
      </Box> : null}

    {(!utils.isDefined(breakdown) || breakdown?.length === 0) ? (
      (!utils.isDefined(breakdown) || isLoading) ?
        <SimpleLoader className="GraphCard-loader" /> :
        <Box className="GraphCard-empty">
          <P>No task data found</P>
        </Box>
    ) : null}
  </StyledCollectionEntitiesTaskBreakdownGraphCard>
});

CollectionEntitiesTaskBreakdownGraphCard.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ])
};

export default CollectionEntitiesTaskBreakdownGraphCard;
